/**
 * useLiveCaptions hook
 *
 * Manages a WebSocket connection to the StreamCaptionsChannel Durable Object.
 * Receives real-time translated captions for a live stream.
 *
 * Usage:
 *   const { latestCaption, isActive, selectedLang, setLanguage } = useLiveCaptions(streamId);
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export interface CaptionEntry {
  id: string;
  text: string;
  lang: string;
  timestamp: string;
  offsetSeconds?: number;
}

export interface DubClipEntry {
  id: string;
  text: string;
  lang: string;
  timestamp: string;
  segmentUrl: string;
  audio: string;
  mimeType: string;
  offsetSeconds?: number;
  r2Key?: string;
  mediaUrl?: string;
}

interface UseLiveCaptionsOptions {
  streamId: number | string | null;
  /** ISO language code (e.g. 'fr', 'es'). Defaults to 'en'. */
  initialLang?: string;
  enabled?: boolean;
  mode?: 'live' | 'recording';
  playbackTimeSec?: number;
  isPlaying?: boolean;
}

interface UseLiveCaptionsResult {
  latestCaption: CaptionEntry | null;
  recentCaptions: CaptionEntry[];
  latestDubClip: DubClipEntry | null;
  recentDubClips: DubClipEntry[];
  isConnected: boolean;
  isActive: boolean;
  selectedLang: string;
  setLanguage: (lang: string) => void;
}

const WS_RECONNECT_DELAY_MS = 3000;
const RECORDING_POLL_PLAYING_MS = 1500;
const RECORDING_POLL_PAUSED_MS = 5000;
const MAX_RECENT = 10;
const RECORDING_WINDOW_LIMIT = 60;
const RECORDING_WINDOW_SEC = 20;

const readCaptionsFromResponse = (payload: unknown): CaptionEntry[] => {
  if (!payload || typeof payload !== 'object') return [];
  const data = (payload as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return [];
  const captions = (data as { captions?: unknown }).captions;
  return Array.isArray(captions) ? (captions as CaptionEntry[]) : [];
};

const readDubClipsFromResponse = (payload: unknown): DubClipEntry[] => {
  if (!payload || typeof payload !== 'object') return [];
  const data = (payload as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return [];
  const clips = (data as { clips?: unknown }).clips;
  return Array.isArray(clips) ? (clips as DubClipEntry[]) : [];
};

export function useLiveCaptions({
  streamId,
  initialLang = 'en',
  enabled = true,
  mode = 'live',
  playbackTimeSec = 0,
  isPlaying = false,
}: UseLiveCaptionsOptions): UseLiveCaptionsResult {
  const [latestCaption, setLatestCaption] = useState<CaptionEntry | null>(null);
  const [recentCaptions, setRecentCaptions] = useState<CaptionEntry[]>([]);
  const [latestDubClip, setLatestDubClip] = useState<DubClipEntry | null>(null);
  const [recentDubClips, setRecentDubClips] = useState<DubClipEntry[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [selectedLang, setSelectedLang] = useState(initialLang);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const langRef = useRef(selectedLang);
  const unmountedRef = useRef(false);
  const recordingPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep lang ref in sync so reconnect uses latest lang
  useEffect(() => { langRef.current = selectedLang; }, [selectedLang]);

  const connect = useCallback(() => {
    if (!streamId || !enabled || unmountedRef.current || mode !== 'live') return;

    // Browser traffic uses same-origin /api in both dev and production.
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = new URL('/api/ws', window.location.origin);
    url.protocol = wsProtocol;
    url.search = `?channel=stream:captions:${streamId}&lang=${langRef.current}`;

    try {
      const ws = new WebSocket(url.toString());
      wsRef.current = ws;

      ws.onopen = () => {
        if (unmountedRef.current) { ws.close(); return; }
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        if (unmountedRef.current) return;
        try {
          const data = JSON.parse(event.data as string);

          switch (data.type) {
            case 'caption': {
              const entry = data.caption as CaptionEntry;
              setLatestCaption(entry);
              setRecentCaptions(prev => {
                const updated = [...prev, entry];
                return updated.slice(-MAX_RECENT);
              });
              break;
            }
            case 'recent_captions': {
              const captions = data.captions as CaptionEntry[];
              setRecentCaptions(captions.slice(-MAX_RECENT));
              if (captions.length > 0) {
                setLatestCaption(captions[captions.length - 1]);
              }
              break;
            }
            case 'dub_clip': {
              const clip = data.clip as DubClipEntry;
              setLatestDubClip(clip);
              setRecentDubClips(prev => {
                const updated = [...prev, clip];
                return updated.slice(-MAX_RECENT);
              });
              break;
            }
            case 'recent_dub_clips': {
              const clips = data.clips as DubClipEntry[];
              setRecentDubClips(clips.slice(-MAX_RECENT));
              if (clips.length > 0) {
                setLatestDubClip(clips[clips.length - 1]);
              }
              break;
            }
            case 'status':
              setIsActive(!!data.active);
              break;
            case 'captions_stopped':
              setIsActive(false);
              break;
            case 'pong':
              break;
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onclose = () => {
        if (unmountedRef.current) return;
        setIsConnected(false);
        // Auto-reconnect
        reconnectTimer.current = setTimeout(connect, WS_RECONNECT_DELAY_MS);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      // Retry after delay if WebSocket construction fails
      reconnectTimer.current = setTimeout(connect, WS_RECONNECT_DELAY_MS);
    }
  }, [streamId, enabled, mode]);

  const fetchRecordingCaptions = useCallback(async () => {
    if (!streamId || !enabled || unmountedRef.current || mode !== 'recording') return;
    try {
      const lang = encodeURIComponent(langRef.current);
      const aroundSec = Number.isFinite(playbackTimeSec) ? Math.max(0, playbackTimeSec) : 0;
      const aroundSecQuery = `&aroundSec=${encodeURIComponent(aroundSec.toFixed(3))}`;
      const windowSecQuery = `&windowSec=${RECORDING_WINDOW_SEC}`;
      const [captionsRes, dubRes] = await Promise.all([
        fetch(`/api/streams/${streamId}/captions/recent?lang=${lang}&limit=${RECORDING_WINDOW_LIMIT}${aroundSecQuery}${windowSecQuery}`),
        fetch(`/api/streams/${streamId}/captions/recent-dub?lang=${lang}&limit=${RECORDING_WINDOW_LIMIT}${aroundSecQuery}${windowSecQuery}`),
      ]);

      if (!captionsRes.ok || !dubRes.ok) {
        setIsConnected(false);
        return;
      }

      const captionsJson = await captionsRes.json().catch(() => null);
      const dubJson = await dubRes.json().catch(() => null);
      const captions = readCaptionsFromResponse(captionsJson);
      const clips = readDubClipsFromResponse(dubJson);

      setRecentCaptions(captions.slice(-RECORDING_WINDOW_LIMIT));
      setLatestCaption(captions.length > 0 ? captions[captions.length - 1] : null);
      setRecentDubClips(clips.slice(-RECORDING_WINDOW_LIMIT));
      setLatestDubClip(clips.length > 0 ? clips[clips.length - 1] : null);
      setIsConnected(true);
      setIsActive(captions.length > 0 || clips.length > 0);
    } catch {
      setIsConnected(false);
    }
  }, [enabled, mode, playbackTimeSec, streamId]);

  // Connect websocket for live mode
  useEffect(() => {
    if (mode !== 'live') return;
    unmountedRef.current = false;
    connect();

    return () => {
      unmountedRef.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on unmount
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, mode]);

  // Poll REST endpoints for recording mode
  useEffect(() => {
    if (mode !== 'recording') return;
    unmountedRef.current = false;

    const tick = async () => {
      await fetchRecordingCaptions();
      if (!unmountedRef.current) {
        recordingPollTimerRef.current = setTimeout(
          tick,
          isPlaying ? RECORDING_POLL_PLAYING_MS : RECORDING_POLL_PAUSED_MS
        );
      }
    };

    void tick();

    return () => {
      unmountedRef.current = true;
      if (recordingPollTimerRef.current) {
        clearTimeout(recordingPollTimerRef.current);
        recordingPollTimerRef.current = null;
      }
    };
  }, [fetchRecordingCaptions, isPlaying, mode]);

  // Change language: close existing connection and reconnect with new lang
  const setLanguage = useCallback((lang: string) => {
    setSelectedLang(lang);
    langRef.current = lang;
    setLatestCaption(null);
    setRecentCaptions([]);
    setLatestDubClip(null);
    setRecentDubClips([]);

    if (wsRef.current) {
      // Send set_lang message if still open
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'set_lang', lang }));
      } else {
        // Reconnect with new lang
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
        setIsConnected(false);
        connect();
      }
    } else if (mode === 'recording') {
      void fetchRecordingCaptions();
    }
  }, [connect, fetchRecordingCaptions, mode]);

  return {
    latestCaption,
    recentCaptions,
    latestDubClip,
    recentDubClips,
    isConnected,
    isActive,
    selectedLang,
    setLanguage,
  };
}
